import { resolverValorDetallado } from './parser.js'
import { funcionesPersonalizadas } from '../funciones/index.js'

function esComillaDoble(c) {
  return c === '"' || c === '“' || c === '”'
}

function esCierreComilla(cierreEsperado, c) {
  if (cierreEsperado === '"') return c === '"'
  if (cierreEsperado === '”') return c === '”'
  if (cierreEsperado === '“') return c === '“'
  return false
}

function cierreParaComilla(c) {
  if (c === '“') return '”'
  return c
}

function extraerLiteral(s) {
  if (!s || s.length < 2) return null
  const apertura = s[0]
  const cierre = s[s.length - 1]
  if (!esComillaDoble(apertura) || !esComillaDoble(cierre)) return null

  const cierreEsperado = cierreParaComilla(apertura)
  if (!esCierreComilla(cierreEsperado, cierre)) return null

  const inner = s.slice(1, -1)
  return apertura === '"' && cierre === '"' ? inner.replace(/\\"/g, '"') : inner
}

function esLiteralNumerico(s) {
  return /^-?(?:\d+|\d*\.\d+)$/.test(String(s || '').trim())
}

function extraerVarDirecta(expresion) {
  const raw = stripWrapper(String(expresion || ''))
  const match = raw.match(/^(v:|i:)(.+)$/)
  if (!match) return null
  return {
    prefijo: match[1].slice(0, -1),
    ruta: match[2].trim(),
  }
}

function expandirAliasConRuta(valor, aliasToVariable, aliasToExpresion) {
  const match = String(valor || '').match(/^([a-zA-Z0-9_]+)(.*)$/)
  if (!match) return null

  const alias = match[1]
  const resto = match[2] || ''
  if (!resto) return null

  const variable = aliasToVariable[alias]
  if (variable) {
    const base = extraerVarDirecta(variable.expresion || `\${v:${variable.fullPath}}`)
    if (base?.ruta) return `\${${base.prefijo}:${base.ruta}${resto}}`
  }

  const expresion = aliasToExpresion[alias]
  if (expresion?.expresion) {
    const base = extraerVarDirecta(expresion.expresion)
    if (base?.ruta) return `\${${base.prefijo}:${base.ruta}${resto}}`
  }

  return null
}

function findTopLevelColon(str) {
  let inQuotes = false
  let cierreQuote = ''
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      escape = true
      continue
    }
    if (!inQuotes && esComillaDoble(c)) {
      inQuotes = true
      cierreQuote = cierreParaComilla(c)
      continue
    }
    if (inQuotes && esCierreComilla(cierreQuote, c)) {
      inQuotes = false
      cierreQuote = ''
      continue
    }
    if (!inQuotes && c === '(') parenDepth++
    if (!inQuotes && c === ')') parenDepth = Math.max(0, parenDepth - 1)
    if (!inQuotes && parenDepth === 0 && c === ':') return i
  }
  return -1
}

function splitArgs(str) {
  const args = []
  let current = ''
  let inQuotes = false
  let cierreQuote = ''
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      current += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }
    if (!inQuotes && esComillaDoble(c)) {
      current += c
      inQuotes = true
      cierreQuote = cierreParaComilla(c)
      continue
    }
    if (inQuotes && esCierreComilla(cierreQuote, c)) {
      current += c
      inQuotes = false
      cierreQuote = ''
      continue
    }
    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }
    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }
    if (!inQuotes && parenDepth === 0 && c === ',') {
      args.push(current.trim())
      current = ''
      continue
    }
    current += c
  }

  if (current.trim().length > 0) args.push(current.trim())
  return args
}

function stripWrapper(str) {
  let s = str.trim()
  if (s.startsWith('${') && s.endsWith('}')) {
    s = s.slice(2, -1).trim()
  }
  return s
}

export function parseExpression(str, errors = []) {
  let s = stripWrapper(str)

  if (s.startsWith('(') && s.endsWith(')')) {
    const inner = s.slice(1, -1).trim()
    return parseExpression(inner, errors)
  }

  if (s.startsWith('v:') || s.startsWith('i:')) {
    return { type: 'var', value: s }
  }

  const colonPos = findTopLevelColon(s)
  if (colonPos !== -1) {
    const name = s.slice(0, colonPos).trim()
    const argsRaw = s.slice(colonPos + 1).trim()
    const args = splitArgs(argsRaw).map((a) => parseArg(a, errors))
    return { type: 'func', name, args }
  }

  const literal = extraerLiteral(s)
  if (literal != null) {
    return { type: 'lit', value: literal }
  }

  if (esLiteralNumerico(s)) {
    return { type: 'lit', value: s }
  }

  if (!s) {
    errors.push({ code: 'empty', message: 'Expresión vacía' })
    return { type: 'lit', value: '' }
  }

  return { type: 'alias', value: s }
}

function parseArg(raw, errors) {
  const s = raw.trim()

  if (s.startsWith('(') && s.endsWith(')')) {
    const inner = s.slice(1, -1).trim()
    return { type: 'func', ...parseExpression(inner, errors) }
  }

  if (s.startsWith('v:') || s.startsWith('i:')) {
    return { type: 'var', value: s }
  }

  const literal = extraerLiteral(s)
  if (literal != null) {
    return { type: 'lit', value: literal }
  }

  if (esLiteralNumerico(s)) {
    return { type: 'lit', value: s }
  }

  const colonPos = findTopLevelColon(s)
  if (colonPos !== -1) {
    errors.push({
      code: 'nested_func_needs_parens',
      message: 'Función directa como argumento debe ir entre paréntesis',
      detail: s,
    })
    return { type: 'lit', value: s }
  }

  return parseExpression(s, errors)
}

export function normalizeNode(
  node,
  aliasToVariable,
  aliasToExpresion,
  pathToVariable,
  errors = []
) {
  if (node.type === 'var') {
    const variableDirecta = extraerVarDirecta(node.value)
    const matchAlias = variableDirecta?.ruta.match(/^([a-zA-Z0-9_]+)(.*)$/)
    const variableAlias = matchAlias ? aliasToVariable[matchAlias[1]] : null

    if (variableAlias?.fullPath) {
      return {
        type: 'var',
        value: `${variableDirecta.prefijo}:${variableAlias.fullPath}${matchAlias[2] || ''}`,
      }
    }

    return node
  }

  if (node.type === 'alias') {
    if (aliasToVariable[node.value]) {
      const variable = aliasToVariable[node.value]
      const vExp = variable.expresion || `\${v:${variable.fullPath}}`
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    if (aliasToExpresion[node.value]) {
      const eExp = aliasToExpresion[node.value].expresion || ''
      return normalizeNode(
        parseExpression(eExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    const aliasExpandido = expandirAliasConRuta(node.value, aliasToVariable, aliasToExpresion)
    if (aliasExpandido) {
      return normalizeNode(
        parseExpression(aliasExpandido, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    if (pathToVariable && pathToVariable[node.value]) {
      const vExp = pathToVariable[node.value].expresion || `\${v:${node.value}}`
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    // Si parece ruta (contiene . o []), tratar como variable directa
    if (/[.\[]/.test(node.value)) {
      const vExp = `\${v:${node.value}}`
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    errors.push({
      code: 'alias_not_found',
      message: 'Alias no encontrado',
      detail: node.value,
    })
    return { type: 'lit', value: node.value }
  }

  if (node.type === 'func') {
    return {
      type: 'func',
      name: node.name,
      args: (node.args || []).map((a) =>
        normalizeNode(a, aliasToVariable, aliasToExpresion, pathToVariable, errors)
      ),
    }
  }

  return node
}

export function stringifyNode(node) {
  if (node.type === 'lit') {
    const safe = String(node.value).replace(/"/g, '\\"')
    return `"${safe}"`
  }
  if (node.type === 'var') return node.value
  if (node.type === 'func') {
    const args = (node.args || []).map((a) => {
      const s = stringifyNode(a)
      if (a.type === 'func') return `(${s})`
      return s
    })
    return `${node.name}:${args.join(',')}`
  }
  if (node.type === 'alias') return node.value
  return ''
}

function crearContextoFuncion(datos, errors, runtimeContext) {
  return {
    ...(runtimeContext || {}),
    datos,
    errors,
    ejecutarExpresion(expresion, datosExpresion = datos, erroresExpresion = errors) {
      const ast = parseExpression(expresion, erroresExpresion)
      return evalNode(ast, datosExpresion, erroresExpresion, runtimeContext)
    },
  }
}

export function evalNode(node, datos, errors = [], runtimeContext = {}) {
  if (node.type === 'lit') return node.value
  if (node.type === 'alias') {
    errors.push({
      code: 'alias_not_found',
      message: 'Alias no encontrado',
      detail: node.value,
    })
    return ''
  }
  if (node.type === 'var') {
    const path = node.value.replace(/^(v:|i:)/, '')
    const resultado = resolverValorDetallado(datos, path)
    if (!resultado.ok) {
      errors.push(resultado.error)
      return ''
    }
    return resultado.value
  }
  if (node.type === 'func') {
    const fn = funcionesPersonalizadas[node.name]
    if (!fn) {
      errors.push({
        code: 'func_not_found',
        message: 'Función no encontrada',
        detail: node.name,
      })
      return ''
    }

    const vals = (node.args || []).map((a) => evalNode(a, datos, errors, runtimeContext))

    try {
      return fn.apply(crearContextoFuncion(datos, errors, runtimeContext), vals)
    } catch (e) {
      errors.push({
        code: e?.code || 'func_error',
        message: e?.code ? e.message : `Error ejecutando ${node.name}`,
        detail: e?.detail || e?.message || String(e),
        functionName: node.name,
      })
      return ''
    }
  }
  return ''
}
